// The morning line. One sentence, forward, named days, consequence once.
// Never "if you skip", never a count of misses. First match wins.
import type { State } from "./types";
import { addDays, dayLong, daysBetween, mondayOf } from "./dates";

export function weekKm(state: State, monday: string): number {
  const sunday = addDays(monday, 6);
  return state.rides.filter((r) => r.d >= monday && r.d <= sunday).reduce((a, r) => a + r.km, 0);
}
export function weekRides(state: State, monday: string): number {
  const sunday = addDays(monday, 6);
  return state.rides.filter((r) => r.d >= monday && r.d <= sunday && r.km >= 15).length;
}
export function longestSince(state: State, fromIso: string): number {
  return state.rides.filter((r) => r.d >= fromIso).reduce((a, r) => Math.max(a, r.km), 0);
}

export function coachLine(state: State, today: string, now = new Date()): string {
  const monday = mondayOf(today);
  // Heavy legs on a ride day beat everything else this morning.
  const ride = state.plan.flatMap((w) => w.days).find((d) => d.date === today && d.type === "ride" && d.status !== "cancelled" && d.status !== "done");
  if (ride && state.log?.legs?.[today] === "heavy") return `Legs heavy. Ride ${ride.start ? `at ${ride.start}` : "today"} easy, no efforts. Cut it short if they stay heavy.`;
  // A ride this week that ended with nothing on Strava overtakes any line written before it (checklist 14).
  const gone = missedSince(state, today, monday, now);
  if (gone) {
    const left = state.plan.flatMap((w) => w.days).filter((d) => d.date >= today && d.date <= addDays(monday, 6) && d.type === "ride" && d.status !== "cancelled" && d.status !== "done" && d.date !== gone.date && d.start)
      .sort((a, b) => (a.date < b.date ? -1 : 1))[0];
    return left ? `${dayLong(gone.date)} is gone. ${dayLong(left.date)} ${left.start} carries the week now.` : `${dayLong(gone.date)} is gone and nothing else is planned this week. Add one ride before Sunday.`;
  }
  // An agreed line written by Claude for this week wins.
  if (state.coach && state.coach.wk === monday) return state.coach.say;

  // Before the ramp's first Monday: say what week 1 holds, by name.
  const first = state.ramp[0];
  if (first && today < first.w) {
    // An agreed line for week 1 wins here too: the day before it starts, it is what week 1 holds.
    if (state.coach && state.coach.wk === first.w) return state.coach.say;
    const w1 = state.plan.find((p) => p.monday === first.w);
    const days = (w1?.days ?? []).filter((d) => d.type === "ride" && d.status !== "cancelled").map((d) => dayLong(d.date));
    const when = daysBetween(today, first.w) <= 7 ? dayLong(first.w) : `Monday ${first.w.slice(8, 10).replace(/^0/, "")}`;
    const list = days.length > 1 ? `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}` : days[0];
    return days.length ? `Week 1 starts ${when}. ${days.length} rides: ${list}.` : `Week 1 starts ${when}.`;
  }

  const plan = state.plan.find((p) => p.monday === monday);
  const ramp = state.ramp.find((r) => r.w === monday);
  const cancelled = plan?.days.find((d) => d.status === "cancelled" && d.date >= today);
  if (cancelled) return `${dayLong(cancelled.date)} is off. The rest of the week holds.`;

  const lastRide = state.rides[0]?.d;
  const twoEmpty = !lastRide || daysBetween(lastRide, monday) > 14;
  const rides = (n: number) => `${n} ${n === 1 ? "ride" : "rides"}`;
  if (twoEmpty && ramp?.rides) return `First week back. The aim is ${rides(ramp.rides)}, not a distance.`;
  // A week counted in rides (week 1, week 2, after a gap) is read in rides even if it carries a km figure.
  if (!ramp || ramp.km == null || ramp.rides) {
    const done = weekRides(state, monday);
    const n = ramp?.rides ?? plan?.days.filter((d) => d.type === "ride" && d.status !== "cancelled").length ?? 3;
    return `${done} of ${rides(n)} this week.`;
  }
  const km = Math.round(weekKm(state, monday));
  const expected = Math.round(ramp.km * Math.min(1, (daysBetween(monday, today) + 1) / 7));
  if (km >= expected) return `On plan. ${km} of ${ramp.km} km this week.`;
  const free = plan?.days.find((d) => d.date > today && d.type === "rest");
  const gap = ramp.km - km;
  return free ? `${gap} km to the week's ${ramp.km}. ${dayLong(free.date)} is free.` : `${gap} km to the week's ${ramp.km}.`;
}

/** The latest ride this week that is over with nothing on Strava, if it ended after the coach's line was written. */
export function missedSince(state: State, today: string, monday: string, now = new Date()) {
  const hm = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(now);
  const rode = (d: string) => state.rides.some((r) => r.d === d && r.km >= 15);
  const said = state.coach && state.coach.wk === monday ? state.coach.at ?? "" : "";
  const over = state.plan.flatMap((w) => w.days).filter((d) => d.type === "ride" && d.status !== "cancelled" && d.status !== "done" && d.end
    && d.date >= monday && (d.date < today || (d.date === today && d.end <= hm)) && !rode(d.date)
    && (!said || said.slice(0, 10) < d.date || (said.slice(0, 10) === d.date && londonHm(said) < d.end!)));
  return over.sort((a, b) => (a.date < b.date ? 1 : -1))[0] ?? null;
}
const londonHm = (iso: string) => new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
